import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Clock, AlertTriangle } from "lucide-react";

/**
 * `/billing/complete?session_id=cs_...` — the page Stripe redirects users to
 * after they finish (or abandon) checkout.
 *
 * We poll `/api/billing/checkout/status/{session_id}` every 2s for up to ~30s.
 * As soon as we see status=paid, we display a celebratory card and bump the
 * sidebar's credit count via a `credits:refresh` event.
 */
export default function BillingCompletePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [phase, setPhase] = useState("polling");   // polling | paid | timeout | failed | error
  const [data, setData] = useState(null);
  const tries = useRef(0);
  const MAX_TRIES = 15;     // 15 × 2s = 30s
  const POLL_MS = 2000;

  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      return;
    }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        const status = r.data?.status;
        if (status === "paid") {
          setData(r.data);
          setPhase("paid");
          window.dispatchEvent(new Event("credits:refresh"));
          window.dispatchEvent(new Event("notifications:refresh"));
          return;
        }
        if (status === "expired" || status === "failed") {
          setPhase("failed");
          return;
        }
      } catch (err) {
        if (cancelled) return;
        // 403/404 are terminal; otherwise treat as transient and keep polling
        const code = err.response?.status;
        if (code === 403 || code === 404) {
          setPhase("error");
          return;
        }
      }
      tries.current += 1;
      if (tries.current >= MAX_TRIES) {
        setPhase("timeout");
        return;
      }
      setTimeout(poll, POLL_MS);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-testid="billing-complete-page">
      <Card className="w-full max-w-md border-gray-200 shadow-lg rounded-2xl">
        <CardContent className="p-8 text-center">
          {phase === "polling" && (
            <>
              <Loader2 className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900">Confirming your payment…</h1>
              <p className="text-sm text-gray-500 mt-2">
                Hang tight — Stripe is letting us know.
              </p>
            </>
          )}
          {phase === "paid" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900">Payment successful</h1>
              {data?.kind === "topup" && (
                <p className="text-sm text-gray-600 mt-2">
                  Added <strong>{data.credits_added}</strong> credits
                </p>
              )}
              {data?.kind === "subscription" && (
                <p className="text-sm text-gray-600 mt-2">
                  You're now on the <strong>{(data.plan_id || "").charAt(0).toUpperCase() + (data.plan_id || "").slice(1)}</strong> plan
                  {data.billing_period === "annual" ? " (billed annually)" : ""}
                  {data.credits_added ? <> — <strong>{data.credits_added}</strong> credits added</> : null}
                </p>
              )}
              <Button
                onClick={() => navigate("/generator")}
                className="mt-5 bg-gray-900 hover:bg-black"
                data-testid="billing-paid-cta"
              >
                Back to Studio
              </Button>
            </>
          )}
          {phase === "failed" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900">Payment didn't go through</h1>
              <p className="text-sm text-gray-500 mt-2">The session expired or was cancelled.</p>
              <Button onClick={() => navigate("/generator")} variant="outline" className="mt-5">
                Try again
              </Button>
            </>
          )}
          {phase === "timeout" && (
            <>
              <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900">Still processing…</h1>
              <p className="text-sm text-gray-500 mt-2">
                Stripe takes a moment sometimes. Check your billing summary in a minute — credits will appear automatically.
              </p>
              <Button onClick={() => navigate("/generator")} className="mt-5 bg-gray-900 hover:bg-black">
                Back to Studio
              </Button>
            </>
          )}
          {phase === "error" && (
            <>
              <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900">Something went wrong</h1>
              <p className="text-sm text-gray-500 mt-2">
                We couldn't find this checkout session. If you were charged, contact support.
              </p>
              <Button onClick={() => navigate("/generator")} variant="outline" className="mt-5">
                Back to Studio
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
